"use client"

import Title from "@/components/common/Typography/Title"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import { TypeTourSearchResultItem } from "@/lib/redux/features/tour/types"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { thunks } from "@/lib/redux/thunks"
import { Button, Col, Empty, Row, Select } from "antd"
import dayjs from "dayjs"
import { useParams, useSearchParams } from "next/navigation"
import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

const formatShortPrice = (value: number, language: string) => {
  if (!Number.isFinite(value) || value <= 0) return ""
  const millions = value / 1_000_000
  const rounded =
    millions >= 10 ? Math.round(millions) : Math.round(millions * 10) / 10
  const suffix = language === "vi" ? "tr" : "m"
  return `${rounded} ${suffix}`
}

const monthKey = (year: number, month: number) =>
  `${year}-${String(month).padStart(2, "0")}`

const ListMonths: FC<{ onChange?: () => void }> = ({ onChange }) => {
  const departureDate = useAppSelector((state) => state.tour.formSearch.departureDate)
  const tours = useAppSelector((state) => state.tour.tours_search.data)
  const searchParams = useSearchParams()
  const params = useParams()
  const { getValidDayjs, getFormatCurrency } = useTETranslation()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()

  const currentYear = dayjs().year()

  const initialDate = useMemo(() => {
    const fromUrl = searchParams.get("start_date")
    const fromStore = departureDate
    const raw = fromStore || fromUrl || dayjs().format("YYYY-MM-DD")
    const parsed = getValidDayjs(raw)
    return parsed.isValid() ? parsed : dayjs()
  }, [departureDate, getValidDayjs, searchParams])

  const [panelYear, setPanelYear] = useState<number>(initialDate.year())

  useEffect(() => {
    setPanelYear(initialDate.year())
  }, [initialDate.year()])

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 3 }, (_, i) => currentYear + i).map((year) => ({
        value: year,
        label: String(year),
      })),
    [currentYear]
  )

  const priceByMonth = useMemo(() => {
    const map: Record<string, number> = {}
    tours.forEach((tour: TypeTourSearchResultItem) => {
      const date = getValidDayjs(tour.startDate)
      if (!date.isValid()) return
      const key = monthKey(date.year(), date.month() + 1)
      const price = parseFloat(tour.priceDefault)
      if (!Number.isFinite(price) || price <= 0) return
      if (!map[key] || price < map[key]) {
        map[key] = price
      }
    })
    return map
  }, [tours, getValidDayjs])

  const selectedDate = departureDate ? getValidDayjs(departureDate) : null
  const selectedYear = selectedDate?.isValid() ? selectedDate.year() : null
  const selectedMonth = selectedDate?.isValid() ? selectedDate.month() + 1 : null

  const firstAvailableMonth =
    panelYear < currentYear
      ? -1
      : panelYear === currentYear
        ? dayjs().month() + 1
        : 1

  const months =
    firstAvailableMonth === -1
      ? []
      : Array.from({ length: 12 }, (_, idx) => idx + 1).filter(
          (m) => m >= firstAvailableMonth
        )

  const getMonthLabel = (month: number) => {
    if (i18n.language === "vi") {
      return `Th${String(month).padStart(2, "0")}`
    }
    return dayjs().year(2000).month(month - 1).locale(i18n.language).format("MMM")
  }

  const onSelectMonth = (month: number) => {
    const formatted = dayjs()
      .year(panelYear)
      .month(month - 1)
      .date(1)
      .format("YYYY-MM-DD")
    dispatch(tourActions.chooseDepartureDate(formatted))
    dispatch(
      thunks.tours_search({
        params: {
          category_id: params?.categoryId,
          start_date: formatted,
          search_mode: 1,
        },
      })
    )
    onChange?.()
  }

  const clearDeparture = () => {
    dispatch(tourActions.chooseDepartureDate(""))
    dispatch(
      thunks.tours_search({
        params: {
          category_id: params?.categoryId,
          start_date: "",
          search_mode: 1,
        },
      })
    )
    onChange?.()
  }

  return (
    <div className="tour-filter-months">
      <Row
        wrap={false}
        className="flights__list--filter-input--box tour-filter-months__title"
        justify="space-between"
        align="middle"
      >
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            {t("tour.departure_calendar")}
          </Title>
        </Col>
        {departureDate && (
          <Col>
            <Button type="text" onClick={clearDeparture}>
              {t("common.reset")}
            </Button>
          </Col>
        )}
      </Row>
      <Row className="flights__list--filter-input--box tour-filter-months__year">
        <Col span={24}>
          <Select
            className="tour-filter-months__year-select"
            style={{ width: "100%" }}
            value={panelYear}
            options={yearOptions}
            onChange={(year) => setPanelYear(year)}
          />
        </Col>
      </Row>
      {firstAvailableMonth === -1 ? (
        <Row className="flights__list--filter-input--box">
          <Col span={24}>
            <Empty description={t("tour.invalid_month")} />
          </Col>
        </Row>
      ) : (
        <Row gutter={[10, 10]} className="tour-filter-months__grid">
          {months.map((month) => {
            const isActive =
              selectedYear === panelYear && selectedMonth === month
            const minPrice = priceByMonth[monthKey(panelYear, month)]
            const priceHint =
              minPrice &&
              (formatShortPrice(minPrice, i18n.language) ||
                getFormatCurrency(minPrice))

            return (
              <Col key={month} span={12}>
                <Button
                  block
                  type="default"
                  className={`tour-filter-months__btn${isActive ? " tour-filter-months__btn--active" : ""}`}
                  onClick={() => onSelectMonth(month)}
                >
                  <span className="tour-filter-months__btn-label">
                    {getMonthLabel(month)}
                  </span>
                  {priceHint && (
                    <span
                      className="tour-filter-months__price-hint"
                      title={getFormatCurrency(minPrice)}
                    >
                      {priceHint}
                    </span>
                  )}
                </Button>
              </Col>
            )
          })}
        </Row>
      )}
    </div>
  )
}

export default ListMonths
